'use client';

import Image from 'next/image';
import { CiLogin } from 'react-icons/ci';
import { useLoginLayout } from '@/features/session/hooks/useLoginLayout';
import { LoginStatus } from '@/features/session/enums/LoginStatus';
import { FullPageLoader } from '@/shared/components/FullPageLoader';
import { commonTranslation, useNamespacedTranslate } from '@/shared/lib/locales';

export function LoginLayout({ children }: { children: React.ReactNode }) {
  const t = useNamespacedTranslate('sessionProvider');

  const { status, isInitialized, handleLogin } = useLoginLayout();

  if (status === LoginStatus.LOADING) {
    return <FullPageLoader text={t('authenticatingUser')} />;
  }

  if (status === LoginStatus.ANONYMOUS) {
    return (
      <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
        <section className="relative flex items-center justify-center overflow-hidden bg-[#0a1328] px-8 py-12">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(34,197,94,0.12),transparent_45%)]" />

          <div className="relative z-10 w-full max-w-md text-center">
            <div className="mb-14 flex justify-center">
              <Image
                src="/assets/svgs/Logo_CJRRenewables.svg"
                alt="CJR Renewables"
                width={170}
                height={54}
                priority
              />
            </div>

            <h2 className="text-5xl font-semibold tracking-tight text-white">{t('welcomeTitle')}</h2>
            <p className="mt-4 text-lg text-slate-300">{t('welcomeDescription')}</p>

            <button
              onClick={handleLogin}
              className="mt-12 inline-flex w-full items-center justify-center rounded-2xl bg-emerald-500 px-6 py-4 text-lg font-semibold text-white transition hover:bg-emerald-600"
            >
              <CiLogin className="mr-3 text-xl" />
              {commonTranslation('login')}
            </button>

            <p className="mt-20 text-sm text-slate-500">{t('copyright')}</p>
          </div>
        </section>

        <section className="relative hidden lg:block">
          <Image
            src="/assets/images/forest.jpg"
            alt={t('forestRoadAlt')}
            fill
            className="object-cover"
            priority
          />
        </section>
      </div>
    );
  }

  if (status === LoginStatus.INITIALIZING || !isInitialized) {
    return <FullPageLoader text={t('initializingSession')} />;
  }

  return <>{children}</>;
}
